import {
  AdminApiError,
  type AdminClient,
  type WebhookDeliveryView,
  type WebhookView,
} from '@apx/admin-sdk';
import { emptyState } from '../empty-state.js';
import { showToast } from '../toast.js';
import { askConfirm } from '../modal.js';
import { fmtDate, fmtDateTime } from '../format.js';

export interface WebhooksPageDeps {
  client: AdminClient;
  integratorId: string;
}

interface EventTypeMeta {
  id: string;
  /** Friendly, admin-facing name shown prominently; `id` is shown subtly. */
  label: string;
  desc: string;
}

// Only events the producer actually emits today. (round.opened / bet.payout /
// jackpot.fired / big_winner were advertised but never emitted — removed so they
// can't be subscribed to. round.settled carries result + per-bet payouts + jackpotWins.)
const EVENT_TYPES: ReadonlyArray<EventTypeMeta> = [
  { id: 'round.settled', label: 'Round result & payouts', desc: 'A round finished — result + all payouts (incl. jackpots) are final.' },
  { id: 'bet.placed', label: 'Bet placed', desc: 'A player placed a bet (fires per bet).' },
];

// Friendly name for a raw event id (fallback to the id itself for unknown/legacy).
const EVENT_LABELS: Record<string, string> = Object.fromEntries(EVENT_TYPES.map((e) => [e.id, e.label]));
const eventLabel = (id: string): string => EVENT_LABELS[id] ?? id;

export async function renderWebhooksPage(root: HTMLElement, deps: WebhooksPageDeps): Promise<void> {
  root.innerHTML = `
    <h1 class="page-title">Webhooks</h1>
    <p class="page-sub">Push notifications from apx to this integrator's backend. The moment a round opens, a bet lands, or a jackpot fires, apx POSTs a signed JSON event to a URL you register here — so the integrator reacts in real time instead of polling the REST API.</p>

    <details class="card wh-info" style="margin-bottom:16px">
      <summary style="cursor:pointer;list-style:none;font-weight:600;font-size:13px;display:flex;justify-content:space-between;align-items:center;gap:10px">
        <span style="display:flex;align-items:center;gap:8px"><span class="wh-info-icon" aria-hidden="true">ⓘ</span>What webhooks are, when to use them, and how to verify them</span>
        <span class="wh-info-cue" style="font-size:11px;white-space:nowrap">Learn more ▸</span>
      </summary>
      <div style="margin-top:14px;font-size:13px;line-height:1.65;color:var(--text-dim)">
        <p><strong style="color:var(--text)">Why:</strong> webhooks are the <em>push</em> alternative to polling. Without them, the integrator has to repeatedly call the REST API asking "anything new?". With them, apx tells the integrator the instant something happens — lower latency, less load, and an exact event log on their side.</p>

        <p style="color:var(--text);font-weight:600;margin:14px 0 4px">When to use them</p>
        <ul style="list-style:disc;margin:0 0 4px;padding-left:20px">
          <li><strong>Real-time audit mirror</strong> — log every bet on the integrator's side as it happens.</li>
          <li><strong>Win / jackpot alerts</strong> — react to wins and jackpots from <code>round.settled</code> (carries per-bet payouts + <code>jackpotWins</code>).</li>
          <li><strong>Leaderboard mirror</strong> — keep their own rankings live without polling.</li>
          <li><strong>Fraud / risk rules</strong> — react to suspicious patterns at <code>bet.placed</code>.</li>
        </ul>

        <p style="color:var(--text);font-weight:600;margin:14px 0 4px">Event reference</p>
        <table style="width:100%;font-size:12px;margin-bottom:6px">
          <tbody>
            ${EVENT_TYPES.map(
              (e) => `<tr><td style="white-space:nowrap;width:1%"><strong style="color:var(--text)">${escapeHtml(e.label)}</strong><br><code class="wh-evt-id">${escapeHtml(e.id)}</code></td><td class="muted">${escapeHtml(e.desc)}</td></tr>`,
            ).join('')}
          </tbody>
        </table>

        <p style="color:var(--text);font-weight:600;margin:14px 0 4px">How delivery works</p>
        <ol style="margin:0 0 4px;padding-left:20px">
          <li>Register a URL below, pick the events, click <strong>Create</strong>.</li>
          <li>A signing secret is shown <em>once</em> — hand it to the integrator securely (only its SHA-256 hash is stored here).</li>
          <li>On each event apx POSTs JSON to the URL, signed <code>HMAC-SHA256(rawBody, secret)</code> in the <code>X-Apx-Signature</code> header (legacy <code>X-Gamehub-Signature</code> is also sent during the brand migration).</li>
          <li>The integrator verifies the signature, then returns <strong>2xx within 5s</strong>. Any non-2xx (or timeout) is retried with exponential backoff; every attempt is visible under <strong>Deliveries</strong>, and a failed one can be redelivered manually.</li>
        </ol>

        <p style="color:var(--text);font-weight:600;margin:14px 0 4px">Verify the signature (integrator side)</p>
        <pre style="background:var(--bg);border:1px solid var(--border);border-radius:6px;padding:10px;overflow:auto;font-size:11.5px;line-height:1.5;color:var(--text)"><code>import { createHmac, timingSafeEqual } from 'node:crypto';

// rawBody MUST be the exact bytes received (do not re-stringify the JSON).
function verify(rawBody, header, secret) {
  const expected = createHmac('sha256', secret).update(rawBody).digest('hex');
  const a = Buffer.from(expected), b = Buffer.from(header ?? '');
  return a.length === b.length && timingSafeEqual(a, b);
}
// app.post('/hooks/apx', (req) =&gt; verify(req.rawBody, req.headers['x-apx-signature'], SECRET))</code></pre>
        <p class="muted" style="font-size:12px;margin:4px 0 0">Use a constant-time compare (<code>timingSafeEqual</code>) and the raw request bytes — re-serializing the parsed JSON will change the signature.</p>
      </div>
    </details>

    <div class="card">
      <h2>Create webhook</h2>
      <label class="field" style="margin-bottom:12px">
        <span class="field-label">Destination URL</span>
        <input id="wh-url" type="url" placeholder="https://integrator.example/hooks/apx" />
      </label>
      <div style="margin-top:12px">
        <div class="muted" style="margin-bottom:8px">Event types — pick what the integrator should be notified about</div>
        <div class="wh-evt-grid">
          ${EVENT_TYPES.map(
            (e) => `<label class="wh-evt-opt">
              <input type="checkbox" value="${e.id}" class="wh-evt" />
              <span><span class="wh-evt-name">${escapeHtml(e.label)}</span><code class="wh-evt-id">${escapeHtml(e.id)}</code><span class="wh-evt-desc">${escapeHtml(e.desc)}</span></span>
            </label>`,
          ).join('')}
        </div>
      </div>
      <div class="row" style="margin-top:14px">
        <button id="wh-create" class="primary">Create</button>
        <span id="wh-msg" class="muted"></span>
      </div>
      <div id="wh-secret-reveal"></div>
    </div>

    <div class="card">
      <h2>Configured webhooks</h2>
      <div id="wh-list"><p class="muted">Loading…</p></div>
    </div>

    <div class="card">
      <h2>Delivery history</h2>
      <p class="muted" style="margin-top:-4px">Recent delivery attempts. Filter by event or status; use <strong>Deliveries</strong> on a row above to switch webhook.</p>
      <div id="wh-deliveries"><p class="muted">Loading…</p></div>
    </div>
  `;

  const listEl = root.querySelector<HTMLElement>('#wh-list')!;
  const msgEl = root.querySelector<HTMLElement>('#wh-msg')!;
  const revealEl = root.querySelector<HTMLElement>('#wh-secret-reveal')!;
  const deliveriesEl = root.querySelector<HTMLElement>('#wh-deliveries')!;

  const DELIV_PAGE_SIZE = 25;
  // Delivery-history view state — which webhook is shown, active filters, page.
  const deliv = {
    webhookId: '' as string,
    status: '' as '' | 'pending' | 'success' | 'failed' | 'dead',
    eventType: '' as string,
    offset: 0,
    total: 0,
  };

  async function refresh(): Promise<void> {
    try {
      const { webhooks } = await deps.client.listWebhooks(deps.integratorId);
      listEl.innerHTML = renderTable(webhooks);
      bindRowButtons();
      // Auto-load delivery history on first load so it's visible without a
      // click — default to the current selection, else the first webhook.
      const stillExists = webhooks.some((w) => w.id === deliv.webhookId);
      const target = stillExists ? deliv.webhookId : webhooks[0]?.id;
      if (target) {
        if (target !== deliv.webhookId) {
          deliv.webhookId = target;
          deliv.offset = 0;
        }
        await loadDeliveries();
      } else {
        deliveriesEl.innerHTML = '<p class="muted">No webhooks yet — create one above to see delivery history.</p>';
      }
    } catch (err) {
      listEl.innerHTML = `<p class="error">${escapeHtml(describe(err))}</p>`;
    }
  }

  function renderTable(rows: WebhookView[]): string {
    if (rows.length === 0) {
      return emptyState({
        icon: 'sparkle',
        title: 'No webhooks subscribed',
        hint:
          'Optional — without webhooks the integrator polls REST for rounds, bets, and payouts. Register a URL above to push events instead. See the guide at the top for when to use them and how to verify signatures.',
      });
    }
    return `<div id="wh-bulk-bar" class="row hidden" style="margin:0 0 10px;padding:8px 10px;background:var(--panel-2);border-radius:var(--radius)">
        <span class="muted"><span id="wh-bulk-count">0</span> selected</span>
        <button id="wh-bulk-pause" class="primary">Pause</button>
        <button id="wh-bulk-resume" class="ghost">Resume</button>
        <button id="wh-bulk-clear" class="ghost" style="margin-left:auto">Clear</button>
      </div>
      <table>
      <thead><tr><th style="width:24px"><input type="checkbox" id="wh-select-all" /></th><th>URL</th><th>Events</th><th>Status</th><th>Created</th><th></th></tr></thead>
      <tbody>${rows
        .map(
          (w) => `<tr>
            <td><input type="checkbox" class="wh-row-check" data-id="${w.id}" /></td>
            <td><code>${escapeHtml(w.url)}</code></td>
            <td>${w.eventTypes.map((e) => `<span class="wh-evt-tag" title="${escapeHtml(e)}">${escapeHtml(eventLabel(e))}</span>`).join('')}</td>
            <td><span class="pill ${w.status === 'active' ? 'live' : 'warn'}">${w.status}</span></td>
            <td><span class="muted">${fmtDate(w.createdAt)}</span></td>
            <td>
              <button data-deliveries="${w.id}">Deliveries</button>
              <button data-test="${w.id}" class="ghost">Test</button>
              <button data-pause="${w.id}" data-current="${w.status}" class="ghost">${w.status === 'active' ? 'Pause' : 'Resume'}</button>
              <button data-revoke="${w.id}" class="ghost">Revoke</button>
              <button data-delete="${w.id}" class="ghost">Delete</button>
            </td>
          </tr>`,
        )
        .join('')}</tbody>
    </table>`;
  }

  function selectedWhIds(): string[] {
    return Array.from(listEl.querySelectorAll<HTMLInputElement>('.wh-row-check:checked')).map(
      (cb) => cb.dataset.id!,
    );
  }

  function refreshWhBulkBar(): void {
    const bar = listEl.querySelector<HTMLElement>('#wh-bulk-bar');
    if (!bar) return;
    const ids = selectedWhIds();
    bar.classList.toggle('hidden', ids.length === 0);
    const countEl = listEl.querySelector<HTMLElement>('#wh-bulk-count');
    if (countEl) countEl.textContent = String(ids.length);
  }

  async function bulkSetWebhookStatus(status: 'active' | 'paused'): Promise<void> {
    const ids = selectedWhIds();
    if (ids.length === 0) return;
    if (status === 'paused' && !(await askConfirm(`Pause ${ids.length} webhook(s)?`))) return;
    const results = await Promise.allSettled(
      ids.map((id) => deps.client.updateWebhook(id, { status })),
    );
    const failed = results.filter((r) => r.status === 'rejected').length;
    if (failed) showToast(`${ids.length - failed} ok, ${failed} failed.`, 'warn');
    else showToast(`${status === 'paused' ? 'Paused' : 'Resumed'} ${ids.length} webhook(s).`, 'success');
    await refresh();
  }

  function bindRowButtons(): void {
    listEl.querySelectorAll<HTMLInputElement>('.wh-row-check').forEach((cb) => {
      cb.addEventListener('change', refreshWhBulkBar);
    });
    listEl.querySelector<HTMLInputElement>('#wh-select-all')?.addEventListener('change', (ev) => {
      const checked = (ev.target as HTMLInputElement).checked;
      listEl.querySelectorAll<HTMLInputElement>('.wh-row-check').forEach((cb) => (cb.checked = checked));
      refreshWhBulkBar();
    });
    listEl.querySelector<HTMLButtonElement>('#wh-bulk-pause')?.addEventListener('click', () => void bulkSetWebhookStatus('paused'));
    listEl.querySelector<HTMLButtonElement>('#wh-bulk-resume')?.addEventListener('click', () => void bulkSetWebhookStatus('active'));
    listEl.querySelector<HTMLButtonElement>('#wh-bulk-clear')?.addEventListener('click', () => {
      listEl.querySelectorAll<HTMLInputElement>('.wh-row-check').forEach((cb) => (cb.checked = false));
      const selAll = listEl.querySelector<HTMLInputElement>('#wh-select-all');
      if (selAll) selAll.checked = false;
      refreshWhBulkBar();
    });
    listEl.querySelectorAll<HTMLButtonElement>('button[data-deliveries]').forEach((btn) => {
      btn.addEventListener('click', () => {
        deliv.webhookId = btn.dataset.deliveries!;
        deliv.offset = 0;
        deliveriesEl.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        void loadDeliveries();
      });
    });
    listEl.querySelectorAll<HTMLButtonElement>('button[data-pause]').forEach((btn) => {
      btn.addEventListener('click', async () => {
        const id = btn.dataset.pause!;
        const next = btn.dataset.current === 'active' ? 'paused' : 'active';
        try {
          await deps.client.updateWebhook(id, { status: next });
          await refresh();
        } catch (err) {
          showToast(describe(err), 'error');
        }
      });
    });
    listEl.querySelectorAll<HTMLButtonElement>('button[data-revoke]').forEach((btn) => {
      btn.addEventListener('click', async () => {
        if (!(await askConfirm('Revoke this webhook? Status will flip to revoked.'))) return;
        try {
          await deps.client.revokeWebhook(btn.dataset.revoke!);
          await refresh();
        } catch (err) {
          showToast(describe(err), 'error');
        }
      });
    });
    listEl.querySelectorAll<HTMLButtonElement>('button[data-test]').forEach((btn) => {
      btn.addEventListener('click', async () => {
        const original = btn.textContent;
        btn.disabled = true;
        btn.textContent = 'Sending…';
        try {
          const r = await deps.client.sendWebhookTest(btn.dataset.test!);
          showToast(`Test ping queued (delivery ${r.id.slice(0, 8)}…). Open Deliveries to see the result.`, 'success');
        } catch (err) {
          showToast(describe(err), 'error');
        } finally {
          btn.disabled = false;
          btn.textContent = original;
        }
      });
    });
    listEl.querySelectorAll<HTMLButtonElement>('button[data-delete]').forEach((btn) => {
      btn.addEventListener('click', async () => {
        if (!(await askConfirm('Delete this webhook? It is hidden and stops receiving events (soft delete — kept for audit history).'))) return;
        try {
          await deps.client.deleteWebhook(btn.dataset.delete!);
          await refresh();
        } catch (err) {
          showToast(describe(err), 'error');
        }
      });
    });
  }

  async function loadDeliveries(): Promise<void> {
    if (!deliv.webhookId) return;
    deliveriesEl.innerHTML = '<p class="muted">Loading deliveries…</p>';
    try {
      const res = await deps.client.listWebhookDeliveries(deliv.webhookId, {
        limit: DELIV_PAGE_SIZE,
        offset: deliv.offset,
        ...(deliv.status ? { status: deliv.status } : {}),
        ...(deliv.eventType ? { eventType: deliv.eventType } : {}),
      });
      deliv.total = res.total ?? res.deliveries.length;
      deliveriesEl.innerHTML = renderDeliveries(res.deliveries);
      bindDeliveryButtons();
    } catch (err) {
      deliveriesEl.innerHTML = `${renderDeliveryControls()}<p class="error">${escapeHtml(describe(err))}</p>`;
      bindDeliveryControls();
    }
  }

  function renderDeliveryControls(): string {
    const opt = (val: string, label: string, sel: string): string =>
      `<option value="${val}"${val === sel ? ' selected' : ''}>${escapeHtml(label)}</option>`;
    const eventOpts = ['', ...EVENT_TYPES.map((e) => e.id)]
      .map((id) => opt(id, id === '' ? 'All events' : eventLabel(id), deliv.eventType))
      .join('');
    const statusOpts = (
      [
        ['', 'All statuses'],
        ['pending', 'Pending'],
        ['success', 'Success'],
        ['failed', 'Failed'],
        ['dead', 'Dead'],
      ] as ReadonlyArray<readonly [string, string]>
    )
      .map(([v, l]) => opt(v, l, deliv.status))
      .join('');
    return `<div class="row" style="gap:8px;align-items:center;margin:0 0 10px;flex-wrap:wrap">
        <span class="muted">Webhook <code>${deliv.webhookId.slice(0, 8)}…</code></span>
        <select id="wh-del-event" class="ghost">${eventOpts}</select>
        <select id="wh-del-status" class="ghost">${statusOpts}</select>
        <button id="wh-del-refresh" class="ghost" title="Reload">↻ Refresh</button>
      </div>`;
  }

  function renderDeliveryPager(shown: number): string {
    const from = deliv.total === 0 ? 0 : deliv.offset + 1;
    const to = deliv.offset + shown;
    const hasPrev = deliv.offset > 0;
    const hasNext = deliv.offset + DELIV_PAGE_SIZE < deliv.total;
    return `<div class="row" style="gap:8px;align-items:center;margin:10px 0 0">
        <span class="muted">${from}–${to} of ${deliv.total}</span>
        <span style="margin-left:auto"></span>
        <button id="wh-del-prev" class="ghost"${hasPrev ? '' : ' disabled'}>← Prev</button>
        <button id="wh-del-next" class="ghost"${hasNext ? '' : ' disabled'}>Next →</button>
      </div>`;
  }

  function renderDeliveries(rows: WebhookDeliveryView[]): string {
    const controls = renderDeliveryControls();
    if (rows.length === 0) {
      const filtered = deliv.status || deliv.eventType;
      return `${controls}<p class="muted">${
        filtered ? 'No deliveries match the current filters.' : 'No deliveries yet for this webhook.'
      }</p>${renderDeliveryPager(0)}`;
    }
    const body = rows
      .map((d) => {
        const cls = d.status === 'success' ? 'live' : d.status === 'pending' ? '' : 'warn';
        const payloadJson = escapeHtml(JSON.stringify(d.payload ?? {}, null, 2));
        return `<tr>
              <td><button class="ghost wh-del-toggle" data-toggle="${d.id}" title="Show delivered payload">▸</button></td>
              <td>${fmtDateTime(d.scheduledAt)}</td>
              <td><code>${d.eventType}</code></td>
              <td><span class="pill ${cls}">${d.status}</span></td>
              <td>${d.attempt}</td>
              <td><span class="muted" style="font-size:11px">${escapeHtml(d.lastError ?? '')}</span></td>
              <td><button data-redeliver="${d.id}" class="ghost">Redeliver</button></td>
            </tr>
            <tr class="wh-del-detail hidden" data-detail="${d.id}">
              <td></td>
              <td colspan="6"><pre style="margin:0;background:var(--bg);border:1px solid var(--border);border-radius:6px;padding:8px;overflow:auto;font-size:11px;line-height:1.5;max-height:280px">${payloadJson}</pre></td>
            </tr>`;
      })
      .join('');
    return `${controls}
      <div style="max-height:50vh;overflow:auto"><table>
        <thead><tr><th></th><th>When</th><th>Event</th><th>Status</th><th>Attempt</th><th>Error</th><th></th></tr></thead>
        <tbody>${body}</tbody>
      </table></div>
      ${renderDeliveryPager(rows.length)}`;
  }

  function bindDeliveryControls(): void {
    deliveriesEl.querySelector<HTMLSelectElement>('#wh-del-event')?.addEventListener('change', (ev) => {
      deliv.eventType = (ev.target as HTMLSelectElement).value;
      deliv.offset = 0;
      void loadDeliveries();
    });
    deliveriesEl.querySelector<HTMLSelectElement>('#wh-del-status')?.addEventListener('change', (ev) => {
      deliv.status = (ev.target as HTMLSelectElement).value as typeof deliv.status;
      deliv.offset = 0;
      void loadDeliveries();
    });
    deliveriesEl.querySelector<HTMLButtonElement>('#wh-del-refresh')?.addEventListener('click', () => void loadDeliveries());
    deliveriesEl.querySelector<HTMLButtonElement>('#wh-del-prev')?.addEventListener('click', () => {
      deliv.offset = Math.max(0, deliv.offset - DELIV_PAGE_SIZE);
      void loadDeliveries();
    });
    deliveriesEl.querySelector<HTMLButtonElement>('#wh-del-next')?.addEventListener('click', () => {
      if (deliv.offset + DELIV_PAGE_SIZE < deliv.total) {
        deliv.offset += DELIV_PAGE_SIZE;
        void loadDeliveries();
      }
    });
  }

  function bindDeliveryButtons(): void {
    bindDeliveryControls();
    deliveriesEl.querySelectorAll<HTMLButtonElement>('button[data-toggle]').forEach((btn) => {
      btn.addEventListener('click', () => {
        const row = deliveriesEl.querySelector<HTMLElement>(`tr[data-detail="${btn.dataset.toggle}"]`);
        if (!row) return;
        const hidden = row.classList.toggle('hidden');
        btn.textContent = hidden ? '▸' : '▾';
      });
    });
    deliveriesEl.querySelectorAll<HTMLButtonElement>('button[data-redeliver]').forEach((btn) => {
      btn.addEventListener('click', async () => {
        try {
          await deps.client.redeliverWebhookDelivery(btn.dataset.redeliver!);
          await loadDeliveries();
        } catch (err) {
          showToast(describe(err), 'error');
        }
      });
    });
  }

  root.querySelector<HTMLButtonElement>('#wh-create')!.addEventListener('click', async () => {
    msgEl.textContent = '';
    revealEl.innerHTML = '';
    const url = (root.querySelector<HTMLInputElement>('#wh-url')!.value || '').trim();
    const eventTypes = Array.from(root.querySelectorAll<HTMLInputElement>('input.wh-evt:checked')).map(
      (cb) => cb.value,
    );
    if (!url || eventTypes.length === 0) {
      msgEl.textContent = 'url + at least one event required';
      msgEl.className = 'error';
      return;
    }
    try {
      const created = await deps.client.createWebhook(deps.integratorId, { url, eventTypes });
      revealEl.innerHTML = `
        <div class="card" style="margin-top:14px;background:var(--panel-2);border-color:var(--accent)">
          <h3 style="margin:0 0 10px;color:var(--accent)">Signing secret — save now, shown once</h3>
          <div class="diff" style="user-select:all;font-size:14px;color:var(--text);background:var(--bg)">${escapeHtml(created.secret)}</div>
          <p class="muted" style="margin-top:8px">Webhook id <code>${created.id}</code>. Each delivery is HMAC-SHA256 signed over the body using this secret.</p>
        </div>
      `;
      msgEl.textContent = 'Created.';
      msgEl.className = 'muted';
      root.querySelector<HTMLInputElement>('#wh-url')!.value = '';
      root.querySelectorAll<HTMLInputElement>('input.wh-evt').forEach((cb) => (cb.checked = false));
      await refresh();
    } catch (err) {
      msgEl.textContent = describe(err);
      msgEl.className = 'error';
    }
  });

  await refresh();
}

function describe(err: unknown): string {
  if (err instanceof AdminApiError) return `${err.code}: ${err.message}`;
  return err instanceof Error ? err.message : String(err);
}

function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) => {
    switch (c) {
      case '&':
        return '&amp;';
      case '<':
        return '&lt;';
      case '>':
        return '&gt;';
      case '"':
        return '&quot;';
      case "'":
        return '&#39;';
      default:
        return c;
    }
  });
}
